// src/components/publishing/publishing-integration-status.tsx
import React, { useEffect, useState } from "react";
import {
  CheckCircle2, AlertTriangle, Clock, Loader2, Plug, Unplug,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import {
  publishingApi, Destination, destinationLabel,
  Integration as PubIntegration, daysUntilExpiry,
} from "@/app/services/publishing/publishing-api";
import { ConfirmDialog } from "@/app/components/shared/confirm-dialog";
import { ConnectDestinationModal } from "./connect-destination-modal";
import { Button } from "../kit";

interface PublishingIntegrationStatusProps {
  destination: Destination;
  brandName?: string;
}

/**
 * Renders inline inside IntegrationDetailPanel (on the Integrations page) for
 * publishing-category integrations. Shows live connection status and surfaces
 * Connect / Reconnect / Disconnect actions. Lazy-fetches status from
 * /api/publishing/integrations.
 */
export function PublishingIntegrationStatus({
  destination, brandName,
}: PublishingIntegrationStatusProps) {
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState<PubIntegration | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const items = await publishingApi.listIntegrations(brandName);
      const match = items.find(i => i.destination === destination) || null;
      setIntegration(match);
    } catch (e: any) {
      // Fail silently on this panel — the page itself will show error state
      // if something is globally broken. Here we just leave the card in
      // "not connected" state.
      setIntegration(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, brandName]);

  const handleDisconnect = async () => {
    if (!integration) return;
    setDisconnecting(true);
    try {
      await publishingApi.disconnect(destination, integration.brand_name);
      toast.success(`${destinationLabel(destination)} disconnected`);
      await load();
    } catch (e: any) {
      toast.error(e.message || "Disconnect failed");
    } finally {
      setDisconnecting(false);
      setDisconnectOpen(false);
    }
  };

  const days = daysUntilExpiry(integration?.expires_at || null);
  const isExpired = !!integration && (
    integration.status !== "active" || (days !== null && days <= 0)
  );
  const isExpiringSoon = integration?.status === "active" && days !== null && days > 0 && days < 7;

  return (
    <>
      <div
        className="p-6 rounded-lg"
        style={{
          backgroundColor: "var(--muted)",
          border: "1px solid var(--border)",
        }}
      >
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <h3
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--foreground)",
            }}
          >
            Connection Status
          </h3>
          <StatusBadge
            loading={loading}
            integration={integration}
            isExpired={isExpired}
            isExpiringSoon={isExpiringSoon}
            daysRemaining={days}
          />
        </div>

        {loading && (
          <div className="flex items-center gap-2" style={{ color: "var(--muted-foreground)" }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span style={{ fontSize: "var(--text-sm)" }}>Checking status…</span>
          </div>
        )}

        {!loading && !integration && (
          <>
            <p
              style={{
                fontSize: "var(--text-base)",
                color: "var(--foreground)",
                lineHeight: "1.6",
                marginBottom: "16px",
              }}
            >
              This destination isn't connected yet. Click <strong>Connect</strong> to
              paste your credentials — we'll verify them against{" "}
              {destinationLabel(destination)} before saving.
            </p>
            <Button onClick={() => setModalOpen(true)} icon={Plug}>Connect {destinationLabel(destination)}
            </Button>
          </>
        )}

        {!loading && integration && (
          <ConnectedView
            integration={integration}
            isExpired={isExpired}
            isExpiringSoon={isExpiringSoon}
            daysRemaining={days}
            onReconnect={() => setModalOpen(true)}
            onDisconnect={() => setDisconnectOpen(true)}
          />
        )}
      </div>

      {modalOpen && (
        <ConnectDestinationModal
          destination={destination}
          brandName={brandName}
          onClose={() => setModalOpen(false)}
          onConnected={() => {
            setModalOpen(false);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={(open) => { if (!open) setDisconnectOpen(false); }}
        onConfirm={handleDisconnect}
        title={`Disconnect ${destinationLabel(destination)}?`}
        description="Your stored credentials will be deleted. You can reconnect at any time, but you'll need to re-enter the token or password."
        confirmLabel={disconnecting ? "Disconnecting…" : "Disconnect"}
        destructive
      />
    </>
  );
}

// ─── Status badge ────────────────────────────────────────────────────────────

function StatusBadge({
  loading, integration, isExpired, isExpiringSoon, daysRemaining,
}: {
  loading: boolean;
  integration: PubIntegration | null;
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysRemaining: number | null;
}) {
  if (loading) return null;

  if (!integration) {
    return (
      <span style={chipSt("var(--muted)", "var(--muted-foreground)", "var(--border)")}>
        Not connected
      </span>
    );
  }
  if (isExpired) {
    return (
      <span style={chipSt("var(--destructive-bg, var(--a-gap-sub))", "var(--destructive, var(--a-gap-t))", "var(--a-gap-sub)")}>
        <AlertTriangle className="w-3 h-3" />
        Expired
      </span>
    );
  }
  if (isExpiringSoon) {
    return (
      <span style={chipSt("var(--st-warn-bg)", "var(--st-warn-t)", "var(--st-warn-bg)")}>
        <Clock className="w-3 h-3" />
        Expires in {daysRemaining}d
      </span>
    );
  }
  return (
    <span style={chipSt("var(--success-bg)", "var(--success-text)", "var(--success-border)")}>
      <CheckCircle2 className="w-3 h-3" />
      Connected
    </span>
  );
}

function chipSt(bg: string, color: string, border: string): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 10px", borderRadius: 999,
    backgroundColor: bg, color, border: `1px solid ${border}`,
    fontSize: "var(--text-xs)",
    fontWeight: "var(--font-weight-semibold)",
    textTransform: "uppercase", letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  };
}

// ─── Connected view (account metadata + actions) ─────────────────────────────

function ConnectedView({
  integration, isExpired, isExpiringSoon, daysRemaining,
  onReconnect, onDisconnect,
}: {
  integration: PubIntegration;
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysRemaining: number | null;
  onReconnect: () => void;
  onDisconnect: () => void;
}) {
  const meta = integration.account_metadata || {};

  const rows: Array<{ label: string; value: React.ReactNode }> = [];

  if (meta.display_name) {
    rows.push({ label: "Account", value: meta.display_name });
  }
  if (meta.username && meta.username !== meta.display_name) {
    rows.push({ label: "Username", value: `@${meta.username}` });
  }
  if (meta.publication_title) {
    rows.push({
      label: "Publication",
      value: meta.publication_url
        ? (
          <a
            href={meta.publication_url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              color: "var(--accent)", textDecoration: "none",
              fontWeight: "var(--font-weight-medium)",
            }}
          >
            {meta.publication_title}
            <ExternalLink className="w-3 h-3" />
          </a>
        )
        : meta.publication_title,
    });
  }
  if (meta.site_url) {
    rows.push({
      label: "Site",
      value: (
        <a
          href={meta.site_url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            color: "var(--accent)", textDecoration: "none",
            fontWeight: "var(--font-weight-medium)",
            overflowWrap: "anywhere", wordBreak: "break-word",
          }}
        >
          {meta.site_url}
          <ExternalLink className="w-3 h-3" />
        </a>
      ),
    });
  }
  if (meta.type === "organization") {
    rows.push({ label: "Post as", value: "Company Page" });
  } else if (meta.type === "person") {
    rows.push({ label: "Post as", value: "Personal profile" });
  }

  if (integration.expires_at) {
    rows.push({
      label: "Token expires",
      value: isExpired
        ? <span style={{ color: "var(--destructive, var(--a-gap-t))", fontWeight: 600 }}>Expired</span>
        : isExpiringSoon
          ? <span style={{ color: "var(--st-warn-t)", fontWeight: 600 }}>in {daysRemaining} day{daysRemaining === 1 ? "" : "s"}</span>
          : new Date(integration.expires_at).toLocaleDateString(),
    });
  }

  rows.push({
    label: "Brand",
    value: integration.brand_name,
  });

  return (
    <>
      {isExpired && (
        <div
          className="p-4 rounded-lg mb-4"
          style={{
            backgroundColor: "var(--a-gap-sub)",
            border: "1px solid var(--a-gap-line)",
          }}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--destructive, var(--a-gap-t))" }} />
            <div>
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  color: "var(--destructive, var(--a-gap-t))",
                  marginBottom: 4,
                }}
              >
                Credentials need attention
              </p>
              <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.5 }}>
                {integration.last_error || "The stored credentials are no longer valid. Reconnect to restore publishing."}
              </p>
            </div>
          </div>
        </div>
      )}

      {isExpiringSoon && !isExpired && (
        <div
          className="p-4 rounded-lg mb-4"
          style={{
            backgroundColor: "var(--st-warn-bg)",
            border: "1px solid var(--st-warn-bd)",
          }}
        >
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--st-warn-t)" }} />
            <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.5 }}>
              Token expires in {daysRemaining} day{daysRemaining === 1 ? "" : "s"}. Reconnect soon to avoid interruption.
            </p>
          </div>
        </div>
      )}

      <dl className="space-y-2 mb-6">
        {rows.map((r, i) => (
          <div key={i} className="flex items-start gap-3">
            <dt
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--muted-foreground)",
                minWidth: 110,
                fontWeight: "var(--font-weight-medium)",
              }}
            >
              {r.label}
            </dt>
            <dd
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--foreground)",
                flex: 1,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {r.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={onReconnect}
          icon={Plug}
        >{isExpired ? "Reconnect" : "Update credentials"}
        </Button>
        <Button
          variant="secondary"
          onClick={onDisconnect}
          icon={Unplug}
        >Disconnect
        </Button>
      </div>
    </>
  );
}